import { Link } from "react-router-dom";
import { Instagram, Linkedin, Twitter, Facebook, ArrowUpRight } from "lucide-react";
import { siteConfig } from "@/lib/siteConfig";
import { useCalendly } from "@/components/CalendlyContext";

const columns = [
  {
    title: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "Work", to: "/work" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    title: "Explore",
    links: [
      { label: "Services", to: "/services" },
      { label: "Industries", to: "/industries" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", to: "/privacy-policy" },
      { label: "Terms of Service", to: "/terms-of-service" },
    ],
  },
];

const socialIcons = [
  { key: "instagram", Icon: Instagram },
  { key: "linkedin", Icon: Linkedin },
  { key: "twitter", Icon: Twitter },
  { key: "facebook", Icon: Facebook },
];

export function Footer() {
  const { openCalendly } = useCalendly();

  return (
    <footer data-testid="site-footer" className="relative border-t border-white/10 bg-[#050505] pt-24 pb-10">
      <div className="container-custom">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 pb-16 border-b border-white/10">
          <div>
            <p className="font-display text-3xl sm:text-4xl font-light tracking-tight text-white max-w-md leading-tight">
              Your next level needs <span className="text-violet-400 font-bold">a better system.</span>
            </p>
          </div>
          <button
            data-testid="footer-book-call-btn"
            onClick={openCalendly}
            className="inline-flex items-center gap-2 shrink-0 bg-white text-black text-xs font-bold tracking-widest uppercase px-7 py-4 hover:bg-violet-500 hover:text-white transition-colors duration-300 w-fit"
          >
            Book A Strategy Call <ArrowUpRight size={16} />
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-10 py-16">
          <div className="col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img src={siteConfig.logoUrl} alt="DULORME LABS logo" className="h-8 w-auto" />
              <span className="font-display font-bold text-white">DULORME LABS<sup className="text-[8px]">™</sup></span>
            </Link>
            <p className="text-sm text-zinc-500 max-w-xs leading-relaxed">
              We build brands. We create growth. We build systems that sell.
            </p>
            <div className="flex gap-4 mt-6">
              {socialIcons.map(({ key, Icon }) => (
                <a
                  key={key}
                  href={siteConfig.social[key]}
                  data-testid={`footer-social-${key}`}
                  aria-label={key}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-500 hover:text-violet-400 transition-colors duration-300"
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>
          {columns.map((col) => (
            <div key={col.title}>
              <p className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-5">{col.title}</p>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="text-sm text-zinc-400 hover:text-white transition-colors duration-300"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <p className="text-xs text-zinc-600">
          © {new Date().getFullYear()} DULORME LABS™. All rights reserved.
        </p>
      </div>
    </footer>
  );
}
